import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, Package, CheckCircle, Clock, Building2, Shield, AlertCircle, MapPin, Layers, Box, Eye, Ban } from "lucide-react";
import { useState, useEffect } from "react";
import { useSearch } from "wouter";
import type { Load, User, LoadItem } from "@shared/schema";
import { useAuth } from "@/hooks/useAuth";
import LoadDetailModal from "@/components/LoadDetailModal";
import MemberLink from "@/components/MemberLink";

const MAPBOX_PUBLIC_TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN;

async function geocodeOriginClient(address: string): Promise<{ lat: number; lng: number } | null> {
  try {
    if (!MAPBOX_PUBLIC_TOKEN || !address.trim()) return null;
    const encoded = encodeURIComponent(address.trim());
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${MAPBOX_PUBLIC_TOKEN}&country=US&limit=1`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const feature = data?.features?.[0];
    if (!feature) return null;
    const [lng, lat] = feature.center;
    return { lat, lng };
  } catch {
    return null;
  }
}

interface PublicUserInfo {
  id: string;
  company: string;
  companyLogo?: string | null;
  dotNumber?: string;
  mcNumber?: string;
  adminVerified: boolean;
  upvoteCount: number;
  completedShipments: number;
  userType: string;
}

interface LoadWithPoster extends Load {
  poster: PublicUserInfo | null;
  items?: LoadItem[];
  isInvited?: boolean;
}

function FreightItemsSummary({ items, fallbackWeight }: { items?: LoadItem[]; fallbackWeight: number }) {
  if (!items || items.length === 0) {
    return (
      <div className="flex items-center gap-2 text-base md:text-sm">
        <Package className="w-4 h-4 text-muted-foreground" />
        <span className="text-muted-foreground">Weight:</span>
        <span className="font-medium">{fallbackWeight.toLocaleString()} lbs</span>
      </div>
    );
  }

  const totalItems = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const totalWeight = items.reduce((sum, item) => sum + ((item.weight || 0) * (item.quantity || 1)), 0);
  const hasHazardous = items.some(item => item.hazardous);
  
  const getItemIcon = (unit: string) => {
    switch (unit) {
      case "Pallet": return <Layers className="w-3 h-3" />;
      case "Crate": return <Box className="w-3 h-3" />;
      default: return <Package className="w-3 h-3" />;
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-base md:text-sm">
        <Package className="w-4 h-4 text-muted-foreground" />
        <span className="text-muted-foreground">Freight:</span>
        <span className="font-medium">
          {totalItems} item{totalItems > 1 ? 's' : ''}, {totalWeight.toLocaleString()} lbs
        </span>
        {hasHazardous && (
          <Badge variant="outline" className="text-yellow-600 border-yellow-600 text-xs ml-1">
            <AlertCircle className="w-3 h-3 mr-1" />
            Hazmat
          </Badge>
        )}
      </div>
      {items.length > 1 && (
        <div className="flex flex-wrap gap-1.5 ml-6">
          {items.map((item, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs">
              {getItemIcon(item.handlingUnit || "Other")}
              <span className="ml-1">
                {item.quantity || 1}x {item.handlingUnit || "Item"}
              </span>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function isMasked(name: string | null | undefined): boolean {
  return !!name && name.includes("●");
}

function PosterBadge({ poster, currentUserVerified }: { poster: PublicUserInfo | null; currentUserVerified: boolean }) {
  if (!poster) return null;

  const roleLabel = poster.userType === "Carrier" ? "Carrier" : 
                    poster.userType === "Broker" ? "Broker" : "Shipper";

  return (
    <div className="flex items-center gap-2 pb-2 mb-2 border-b flex-wrap">
      <div className="flex items-center gap-2 min-w-0">
        {isMasked(poster.company) ? (
          <div className="flex items-center gap-2" data-testid="text-poster-masked">
            <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span
              className="text-sm font-medium select-none"
              style={{ filter: "blur(4px)", userSelect: "none", pointerEvents: "none" }}
            >
              {poster.company}
            </span>
          </div>
        ) : (
          <MemberLink
            userId={poster.id}
            name={poster.company || "Unknown"}
            companyLogo={poster.companyLogo}
            showAvatar
            avatarSize="sm"
            className="text-sm font-medium truncate"
            data-testid="link-poster-company"
          />
        )}
        {!isMasked(poster.company) && (
          <Badge variant="secondary" className="text-xs shrink-0">
            {roleLabel}
          </Badge>
        )}
      </div>
      {!isMasked(poster.company) && poster.adminVerified && (
        <Badge variant="outline" className="text-green-600 border-green-600 text-xs ml-auto shrink-0">
          <CheckCircle className="w-3 h-3 mr-1" />
          Platform Verified
        </Badge>
      )}
    </div>
  );
}

interface FindLoadsProps {
  adminMode?: boolean;
}

export default function FindLoads({ adminMode = false }: FindLoadsProps) {
  const [searchOrigin, setSearchOrigin] = useState("");
  const [searchDestination, setSearchDestination] = useState("");
  const [searchWeight, setSearchWeight] = useState("");
  const [searchCompany, setSearchCompany] = useState("");
  const [selectedLoad, setSelectedLoad] = useState<LoadWithPoster | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [radiusEnabled, setRadiusEnabled] = useState(false);
  const [radiusMiles, setRadiusMiles] = useState<50 | 75>(50);
  const [adminStatus, setAdminStatus] = useState("all");
  const [geocodedOrigin, setGeocodedOrigin] = useState<{ lat: number; lng: number; address: string } | null>(null);
  const { user } = useAuth();
  const searchString = useSearch();
  const highlightLoadId = new URLSearchParams(searchString).get("highlight");
  const loadsEndpoint = adminMode ? '/api/admin/public-load-board' : '/api/loads';

  const { data: currentUser } = useQuery<User>({
    queryKey: ['/api/auth/user'],
  });

  const isCurrentUserBroker = currentUser?.userType === "Broker";

  const { data: verificationSetting, isLoading: verificationSettingLoading } = useQuery<{ verificationRequired: boolean }>({
    queryKey: ['/api/site-settings/verification-required'],
  });

  useEffect(() => {
    if (!radiusEnabled || !searchOrigin.trim()) {
      setGeocodedOrigin(null);
      return;
    }
    let cancelled = false;
    geocodeOriginClient(searchOrigin).then(coords => {
      if (!cancelled && coords) {
        setGeocodedOrigin({ ...coords, address: searchOrigin.trim() });
      } else if (!cancelled) {
        setGeocodedOrigin(null);
      }
    });
    return () => { cancelled = true; };
  }, [searchOrigin, radiusEnabled]);

  const { data: loads, isLoading } = useQuery<LoadWithPoster[]>({
    queryKey: [loadsEndpoint, {
      status: adminMode ? adminStatus : 'Available',
      company: searchCompany,
      radius: radiusEnabled ? radiusMiles : undefined,
      searchLat: geocodedOrigin?.lat,
      searchLng: geocodedOrigin?.lng,
    }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (!adminMode || adminStatus !== "all") {
        params.set('status', adminMode ? adminStatus : 'Available');
      }
      if (searchCompany.trim()) params.set('company', searchCompany.trim());
      if (radiusEnabled && geocodedOrigin) {
        params.set('radius', String(radiusMiles));
        params.set('searchLat', String(geocodedOrigin.lat));
        params.set('searchLng', String(geocodedOrigin.lng));
      }
      return fetch(`${loadsEndpoint}?${params}`, { credentials: 'include' }).then(async (response) => {
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.error || "Failed to fetch loads");
        }
        return response.json();
      });
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const isUserVerified = verificationSettingLoading || verificationSetting?.verificationRequired === false || currentUser?.verificationStatus === "verified";

  const filteredLoads = loads?.filter((load: LoadWithPoster) => {
    const matchesOrigin = radiusEnabled
      ? true
      : !searchOrigin || load.origin.toLowerCase().includes(searchOrigin.toLowerCase());
    const matchesDestination = !searchDestination || 
      load.destination.toLowerCase().includes(searchDestination.toLowerCase());
    const matchesWeight = !searchWeight || 
      (load.weight && load.weight >= parseInt(searchWeight));
    const matchesCompany = !searchCompany ||
      (load.poster?.company ?? "").toLowerCase().includes(searchCompany.toLowerCase());

    return matchesOrigin && matchesDestination && matchesWeight && matchesCompany;
  });

  const sortedLoads = [...(filteredLoads ?? [])].sort((a, b) => {
    if (a.isInvited && !b.isInvited) return -1;
    if (!a.isInvited && b.isInvited) return 1;
    return 0;
  });

  useEffect(() => {
    if (!highlightLoadId || !sortedLoads.length) return;
    const highlighted = sortedLoads.find(l => l.id === highlightLoadId);
    if (highlighted) {
      setSelectedLoad(highlighted);
      setModalOpen(true);
    }
  }, [highlightLoadId, sortedLoads.length]);

  const getStatusBadge = (load: Load) => {
    if (load.status === "Completed") {
      return (
        <Badge variant="default" className="bg-green-600">
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </Badge>
      );
    }
    if (load.status === "Confirmed" || load.claimedByUserId) {
      return (
        <Badge variant="outline">
          <Clock className="w-3 h-3 mr-1" />
          {load.status === "Confirmed" ? "Confirmed" : "In Progress"}
        </Badge>
      );
    }
    if (load.status !== "Available") {
      return <Badge variant="secondary">{load.status}</Badge>;
    }
    return (
      <Badge variant="default">
        Available
      </Badge>
    );
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">
          {adminMode ? "Public Load Board" : "Find Loads"}
        </h1>
        <p className="text-muted-foreground">
          {adminMode
            ? "Review the public-facing presentation of every posted load"
            : "Browse available freight loads and connect with shippers"}
        </p>
      </div>

      {adminMode && (
        <div
          className="mb-8 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950"
          data-testid="banner-admin-load-board"
        >
          <p className="font-semibold">Admin View</p>
          <p className="text-sm">
            Showing Admin-visible loads unmasked, including broker-owned listings.
          </p>
        </div>
      )}

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Search Filters</CardTitle>
          <CardDescription>Filter loads by origin, destination, weight, or company</CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`grid grid-cols-1 md:grid-cols-2 ${adminMode ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-4`}>
            <div className="space-y-2">
              <Label htmlFor="search-origin">Origin</Label>
              <Input
                id="search-origin"
                placeholder="e.g., Los Angeles, CA"
                value={searchOrigin}
                onChange={(e) => setSearchOrigin(e.target.value)}
                data-testid="input-search-origin"
              />
              <div className="flex items-center gap-2 pt-1">
                <Switch
                  id="radius-toggle-loads"
                  checked={radiusEnabled}
                  onCheckedChange={setRadiusEnabled}
                  data-testid="switch-radius-loads"
                />
                <Label htmlFor="radius-toggle-loads" className="text-sm font-normal cursor-pointer">
                  Search 50-75 miles radius from origin
                </Label>
              </div>
              {radiusEnabled && (
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setRadiusMiles(50)}
                    className={`flex-1 text-sm py-1 px-3 rounded-md border transition-colors ${radiusMiles === 50 ? 'bg-primary text-primary-foreground border-primary' : 'border-border bg-background hover-elevate'}`}
                    data-testid="button-radius-50-loads"
                  >
                    50 miles
                  </button>
                  <button
                    type="button"
                    onClick={() => setRadiusMiles(75)}
                    className={`flex-1 text-sm py-1 px-3 rounded-md border transition-colors ${radiusMiles === 75 ? 'bg-primary text-primary-foreground border-primary' : 'border-border bg-background hover-elevate'}`}
                    data-testid="button-radius-75-loads"
                  >
                    75 miles
                  </button>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="search-destination">Destination</Label>
              <Input
                id="search-destination"
                placeholder="e.g., New York, NY"
                value={searchDestination}
                onChange={(e) => setSearchDestination(e.target.value)}
                data-testid="input-search-destination"
              />
            </div>
            <div>
              <Label htmlFor="search-weight">Min Weight (lbs)</Label>
              <Input
                id="search-weight"
                type="number"
                placeholder="e.g., 5000"
                value={searchWeight}
                onChange={(e) => setSearchWeight(e.target.value)}
                data-testid="input-search-weight"
              />
            </div>
            <div>
              <Label htmlFor="search-company">Company Name</Label>
              <Input
                id="search-company"
                placeholder="e.g., ABC Shipper LLC"
                value={searchCompany}
                onChange={(e) => setSearchCompany(e.target.value)}
                data-testid="input-search-company"
              />
            </div>
            {adminMode && (
              <div className="space-y-2">
                <Label htmlFor="admin-load-status">Status</Label>
                <Select value={adminStatus} onValueChange={setAdminStatus}>
                  <SelectTrigger id="admin-load-status" data-testid="select-admin-load-status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Available">Live / Current (Available)</SelectItem>
                    <SelectItem value="Confirmed">Confirmed</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                    <SelectItem value="Expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {radiusEnabled && geocodedOrigin && (
        <p className="text-base md:text-sm text-muted-foreground mb-4" data-testid="text-radius-label-loads">
          Showing results within {radiusMiles} miles of {geocodedOrigin.address}
        </p>
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading loads...</p>
        </div>
      ) : sortedLoads && sortedLoads.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedLoads.map((load) => (
            <div key={load.id} style={{ width: '100%', minWidth: 0 }}>
              {load.isInvited && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-3 mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600 mt-0.5 shrink-0"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  <div>
                    <p className="text-sm font-medium text-blue-800 whitespace-nowrap overflow-hidden text-overflow-ellipsis">{load.poster?.company ?? 'Your partner'} invited you to bid</p>
                    <p className="text-xs text-blue-600 mt-0.5">This load was shared directly with you</p>
                  </div>
                </div>
              )}
            <Card
              className={`w-full overflow-hidden hover-elevate cursor-pointer${load.isInvited ? ' border-blue-200' : ''}`}
              data-testid={`card-load-${load.id}`}
              onClick={() => { setSelectedLoad(load); setModalOpen(true); }}
            >
              {load.isInvited && (
                <div className="px-6 pt-4 pb-0">
                  <span className="bg-blue-600 text-white text-xs font-medium px-3 py-0.5 rounded-full">
                    Invited to bid
                  </span>
                </div>
              )}
              <CardHeader>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <PosterBadge poster={load.poster} currentUserVerified={isUserVerified} />
                  <div className="flex items-center gap-2 shrink-0">
                    {load.loadIdentifier && (
                      <Badge variant="outline" className="font-mono text-xs" data-testid={`text-load-id-${load.id}`}>
                        {load.loadIdentifier}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">
                      {load.origin} → {load.destination}
                    </CardTitle>
                    <CardDescription>
                      {load.weight.toLocaleString()} lbs
                    </CardDescription>
                  </div>
                  <div className="flex flex-col gap-1 items-end shrink-0">
                    {getStatusBadge(load)}
                    {isCurrentUserBroker && load.poster?.userType === "Broker" && load.userId !== user?.id && (
                      <Badge variant="outline" className="text-xs text-muted-foreground" data-testid={`badge-carrier-only-${load.id}`}>
                        <Ban className="w-3 h-3 mr-1" />
                        Carrier only
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Location Details */}
                <div className="space-y-2">
                  <div className="flex items-start gap-2 text-base md:text-sm">
                    <MapPin className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-muted-foreground">Pickup: </span>
                      <span className="font-medium" data-testid={`text-origin-${load.id}`}>{load.origin}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-base md:text-sm">
                    <MapPin className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-muted-foreground">Delivery: </span>
                      <span className="font-medium" data-testid={`text-destination-${load.id}`}>{load.destination}</span>
                    </div>
                  </div>
                </div>

                <Separator />

                <FreightItemsSummary items={load.items} fallbackWeight={load.weight || 0} />
                
                {load.pickupDate && (
                  <div className="flex items-center gap-2 text-base md:text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Pickup Date:</span>
                    <span className="font-medium">
                      {new Date(load.pickupDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {load.deliveryDate && (
                  <div className="flex items-center gap-2 text-base md:text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Delivery Date:</span>
                    <span className="font-medium">
                      {new Date(load.deliveryDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {load.details && (
                  <>
                    <Separator />
                    <p className="text-base md:text-sm text-muted-foreground">{load.details}</p>
                  </>
                )}

                <Separator />
                
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={(e) => { e.stopPropagation(); setSelectedLoad(load); setModalOpen(true); }}
                  data-testid={`button-view-details-${load.id}`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </Button>
              </CardContent>
            </Card>
            </div>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No loads found</h3>
            <p className="text-muted-foreground">
              {searchOrigin || searchDestination || searchWeight || searchCompany
                ? "Try adjusting your search filters"
                : "No loads are currently available"}
            </p>
          </CardContent>
        </Card>
      )}

      <LoadDetailModal
        load={selectedLoad}
        open={modalOpen}
        onOpenChange={setModalOpen}
        currentUserId={user?.id || undefined}
        currentUserType={currentUser?.userType || undefined}
        currentUserVerified={isUserVerified}
        readOnly={adminMode}
      />
    </div>
  );
}
